import React from "react";
import ContactForm from "../ui/ContactForm";

const Contact = () => {
  const contactInfo = [
    {
      icon: "fas fa-map-marker-alt",
      title: "Our Location",
      content: "Windhoek, Namibia",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: "fas fa-phone",
      title: "Phone Number",
      content: "+264 85 2000 821",
      link: "tel:+264852000821",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: "fas fa-envelope",
      title: "Email Address",
      content: "stashmasterstradingcc@gmail.com",
      link: "mailto:stashmasterstradingcc@gmail.com",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: "fab fa-whatsapp",
      title: "WhatsApp",
      content: "Chat with us directly",
      link: "http://wa.me/264852000821",
      gradient: "from-green-600 to-teal-500",
    },
  ];

  const workingHours = [
    {
      day: "Monday - Friday",
      hours: "08:00 AM - 05:00 PM",
      days: [1, 2, 3, 4, 5],
      open: "08:00",
      close: "17:00",
    },
    {
      day: "Saturday",
      hours: "09:00 AM - 02:00 PM",
      days: [6],
      open: "09:00",
      close: "14:00",
    },
    {
      day: "Sunday",
      hours: "Closed",
      days: [0],
      open: null,
      close: null,
    },
  ];

  const getWindhoekTimeParts = () => {
    const now = new Date();

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Africa/Windhoek",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);

    const weekdayMap = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };

    const weekday = weekdayMap[parts.find((p) => p.type === "weekday")?.value];
    const hour = Number(parts.find((p) => p.type === "hour")?.value || 0);
    const minute = Number(parts.find((p) => p.type === "minute")?.value || 0);

    return {
      weekday,
      currentMinutes: hour * 60 + minute,
    };
  };

  const isCurrentlyOpen = (schedule) => {
    const { weekday, currentMinutes } = getWindhoekTimeParts();

    if (!schedule.days.includes(weekday) || !schedule.open || !schedule.close) {
      return false;
    }

    const [openHour, openMinute] = schedule.open.split(":").map(Number);
    const [closeHour, closeMinute] = schedule.close.split(":").map(Number);

    const openMinutes = openHour * 60 + openMinute;
    const closeMinutes = closeHour * 60 + closeMinute;

    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };

  const businessOpenNow = workingHours.some((schedule) =>
    isCurrentlyOpen(schedule),
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 bg-gradient-to-br from-gray-900 via-primary to-accent">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: "30px",
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-5 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-6">
              <i className="fas fa-comments mr-2"></i>
              GET IN TOUCH
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Let's{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-cyan-200">
                Create
              </span>{" "}
              Together
            </h1>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Start your journey with expert craftsmanship. Share your vision
              and let's bring it to life.
            </p>

            <div className="flex flex-wrap justify-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">24h</div>
                <div className="text-white/60 text-sm">Response Time</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-white">6</div>
                <div className="text-white/60 text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16 -mt-10 relative z-20">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary to-accent rounded-lg flex items-center justify-center">
                  <i className="fas fa-info-circle text-white"></i>
                </div>
                Contact Information
              </h2>

              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <a
                    key={index}
                    href={info.link || "#"}
                    className={`group block p-4 rounded-xl border border-gray-200 hover:border-transparent transition-all duration-300 hover:scale-102 ${
                      info.link ? "cursor-pointer" : "cursor-default"
                    }`}
                    {...(info.link && {
                      target: "_blank",
                      rel: "noopener noreferrer",
                    })}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-r ${info.gradient} flex items-center justify-center transform group-hover:scale-110 transition-transform`}
                      >
                        <i className={`${info.icon} text-white text-lg`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                          {info.title}
                        </h3>
                        <p className="text-gray-600 text-sm">{info.content}</p>
                      </div>
                      {info.link && (
                        <i className="fas fa-arrow-right text-gray-400 group-hover:text-primary transform group-hover:translate-x-2 transition-all"></i>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Working Hours Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <i className="fas fa-clock"></i>
                </div>
                Working Hours
              </h3>

              <div className="space-y-4">
                {workingHours.map((hour, index) => {
                  const openNow = isCurrentlyOpen(hour);

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between py-3 border-b border-white/10 last:border-0"
                    >
                      <div>
                        <div className="font-medium">{hour.day}</div>
                        <div className="text-sm text-gray-300">
                          {hour.hours}
                        </div>
                      </div>
                      <div
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          openNow
                            ? "bg-green-500/20 text-green-300"
                            : "bg-red-500/20 text-red-300"
                        }`}
                      >
                        {openNow ? "Open Now" : "Closed"}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-2">Currently</div>
                  <div
                    className={`text-2xl font-bold ${
                      businessOpenNow ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {businessOpenNow ? "Available" : "Closed"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    We respond within 24 hours
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-8">
                <div className="text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    Start Your{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                      Project
                    </span>
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    Fill out the form below and our team will get back to you
                    with a custom quote and design consultation.
                  </p>
                </div>

                <div className="mb-10">
                  <div className="flex items-center justify-between relative">
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -translate-y-1/2"></div>
                    {["Details", "Project", "Review"].map((step, index) => (
                      <div key={index} className="relative z-10">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div className="text-xs font-semibold text-gray-700 mt-2">
                          {step}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <ContactForm />

                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-shield-alt text-white"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">
                          Secure & Confidential
                        </h4>
                        <p className="text-sm text-gray-600">
                          Your information is protected and will only be used to
                          provide you with the best service.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold text-gray-900">
                      Prefer to talk?
                    </div>
                    <div className="text-gray-600 text-sm">
                      Call us directly for immediate assistance
                    </div>
                  </div>
                  <a
                    href="tel:+264852000821"
                    className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center gap-3"
                  >
                    <i className="fas fa-phone"></i>
                    Call Now: +264 85 2000 821
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-center gap-4">
              <a
                href="https://www.facebook.com/share/189JKAUPAP/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:scale-110 hover:shadow-lg transition-all duration-300"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f text-xl"></i>
              </a>
              <a
                href="http://wa.me/264852000821"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-green-500 text-white rounded-xl flex items-center justify-center hover:scale-110 hover:shadow-lg transition-all duration-300"
                aria-label="WhatsApp"
              >
                <i className="fab fa-whatsapp text-xl"></i>
              </a>
              <a
                href="https://www.instagram.com/stash_masters__/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl flex items-center justify-center hover:scale-110 hover:shadow-lg transition-all duration-300"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
